import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import MUIDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { SxProps } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { noop } from 'lodash-es';
import { ReactElement } from 'react';

import { PropWithTestId, PropsWithChildren, Sxable } from 'library/types';

import { DialogCloseProps } from '../dialogs/BaseDialog';

export const DRAWER_SIDE_SPACE = 4;

export const DrawerContainer = ({ children }: PropsWithChildren) => (
	<Box
		sx={{
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',

			width: (theme) => ({
				xs: '100vw',
				sm: '70vw',
				md: theme.spacing(80),
			}),
		}}>
		{children}
	</Box>
);

interface CloseButtonProps {
	handleClose: () => void;
}

export const CloseButton = ({ handleClose }: CloseButtonProps) => (
	<IconButton
		onClick={handleClose}
		sx={(theme) => ({
			position: 'absolute',
			right: theme.spacing(2),
			top: theme.spacing(2),
			background: '#FFFFFF70',
			zIndex: 1,
			'&:hover': {
				background: '#FFFFFF90',
			},
		})}>
		<Close />
	</IconButton>
);

interface DrawerProps
	extends PropsWithChildren, Sxable, PropWithTestId, DialogCloseProps {
	childrenSx?: SxProps;
	title: string | ReactElement;
	open?: boolean;
}

export const Drawer = ({
	children,
	childrenSx = { p: DRAWER_SIDE_SPACE, pt: 0 },
	dataTestId,
	onClose = noop,
	open = true,
	sx,
	title,
}: DrawerProps) => {
	const {
		palette: {
			gray: { main },
		},
	} = useTheme();

	return (
		<MUIDrawer
			data-testid={dataTestId}
			open={open}
			anchor="right"
			onClose={onClose}
			slotProps={{
				paper: { sx },
			}}>
			<DrawerContainer>
				<CloseButton handleClose={onClose} />
				<Box sx={{ backgroundColor: main, paddingY: 3 }}>
					<Typography
						variant="h3"
						sx={{
							marginLeft: 4,
							marginRight: 8,
						}}>
						{title}
					</Typography>
				</Box>
				<Box
					sx={[
						{
							overflow: 'auto',
						},
						...(Array.isArray(childrenSx)
							? childrenSx
							: [childrenSx]),
					]}>
					{children}
				</Box>
			</DrawerContainer>
		</MUIDrawer>
	);
};
